<template>
  <v-container fluid class="mt-4 alerts-page px-6 pb-12">
    <!-- Page Header -->
    <div
      class="d-flex align-center justify-space-between mb-6 flex-wrap"
      style="gap: 16px"
    >
      <div>
        <h1 class="text-h4 font-weight-bold primary--text mb-1">
          Quản Lý Cảnh Báo & Vi Phạm
        </h1>
        <p class="subtitle-2 grey--text mb-0">
          Theo dõi, cấu hình và xử lý các trường hợp vi phạm quy định học tập
        </p>
      </div>
      <div
        v-if="activeTab !== 'settings'"
        class="d-flex align-center flex-wrap"
        style="gap: 8px"
      >
        <!-- Class Filter -->
        <v-autocomplete
          v-model="selectedClasses"
          :items="classes"
          item-text="className"
          item-value="id"
          label="Lọc theo lớp"
          multiple
          outlined
          dense
          hide-details
          style="max-width: 250px; min-width: 180px"
          placeholder="Tất cả các lớp"
          @change="handleFilterChange"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip
              v-if="index === 0"
              small
              class="ma-0 text-truncate"
              style="max-width: 120px"
            >
              <span>{{ item.className }}</span>
            </v-chip>
            <span
              v-if="index === 1"
              class="grey--text text--darken-3 caption ml-2 font-weight-bold"
            >
              +{{ selectedClasses.length - 1 }}
            </span>
          </template>
        </v-autocomplete>

        <!-- Date From Picker -->
        <v-menu
          v-model="fromDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateRangeFrom"
              label="Từ ngày"
              prepend-inner-icon="mdi-calendar"
              readonly
              outlined
              dense
              hide-details
              style="width: 160px"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="dateRangeFrom"
            :first-day-of-week="1"
            :max="lastClosedDate"
            locale="vi"
            @input="
              fromDateMenu = false;
              handleFilterChange();
            "
          />
        </v-menu>

        <!-- Date To Picker -->
        <v-menu
          v-model="toDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateRangeTo"
              label="Đến ngày"
              prepend-inner-icon="mdi-calendar"
              readonly
              outlined
              dense
              hide-details
              style="width: 160px"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="dateRangeTo"
            :first-day-of-week="1"
            :max="lastClosedDate"
            locale="vi"
            @input="
              toDateMenu = false;
              handleFilterChange();
            "
          />
        </v-menu>

        <!-- Clear Filters Button -->
        <v-btn
          small
          text
          icon
          color="grey darken-2"
          title="Xóa bộ lọc"
          @click.stop="resetFilters"
        >
          <v-icon small>mdi-filter-off</v-icon>
        </v-btn>

        <!-- Refresh Button -->
        <v-btn color="primary" outlined small @click="fetchAlerts">
          <v-icon left small>mdi-refresh</v-icon>
          Tải lại
        </v-btn>
      </div>
    </div>
    <v-row class="mb-6 kpi-row" dense>
      <v-col
        v-for="kpi in kpis"
        :key="kpi.value"
        cols="6"
        sm="4"
        md="2"
        class="pa-2"
      >
        <v-card
          :class="[
            'kpi-card',
            activeTab === kpi.value && 'active',
            kpi.value + '-kpi',
          ]"
          @click="activeTab = kpi.value"
          outlined
          elevation="0"
          ripple
        >
          <v-card-text class="d-flex align-center pa-4">
            <v-avatar size="44" :color="kpi.bgColor" class="mr-3">
              <v-icon :color="kpi.iconColor" size="24">{{ kpi.icon }}</v-icon>
            </v-avatar>
            <div class="overflow-hidden">
              <div
                class="caption text-truncate grey--text text--darken-1 font-weight-medium mb-1"
              >
                {{ kpi.title }}
              </div>
              <div
                class="text-h5 font-weight-black"
                :class="kpi.textColor + '--text'"
              >
                {{ kpi.count }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Manual Violation Dialog -->
    <v-dialog v-model="manualViolationDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline font-weight-bold"
          >Log Vi Phạm Thủ Công</v-card-title
        >
        <v-divider />
        <v-card-text class="pt-5">
          <v-form ref="violationForm" @submit.prevent="submitManualViolation">
            <v-autocomplete
              v-model="manualViolation.userId"
              :items="students"
              item-text="fullName"
              item-value="id"
              label="Chọn học sinh"
              outlined
              dense
              hide-details
              class="mb-4"
            />
            <v-select
              v-model="manualViolation.alertType"
              :items="alertTypeOptions"
              item-text="text"
              item-value="value"
              label="Loại vi phạm"
              outlined
              dense
              hide-details
              class="mb-4"
            />
            <v-text-field
              v-model="manualViolation.detail"
              label="Ghi chú"
              outlined
              dense
              hide-details
              class="mb-4"
            />
            <v-select
              v-model="manualViolation.severity"
              :items="[
                { text: 'Cảnh báo', value: 'warning' },
                { text: 'Nghiêm trọng', value: 'critical' },
              ]"
              item-text="text"
              item-value="value"
              label="Mức độ"
              outlined
              dense
              hide-details
            />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions class="py-3 px-4">
          <v-spacer />
          <v-btn text @click="manualViolationDialog = false">Hủy</v-btn>
          <v-btn
            color="primary"
            :loading="submittingViolation"
            @click="submitManualViolation"
          >
            Lưu
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Nothing is queried until a class is picked: running every class at once
         scans all students and takes minutes. -->
    <v-alert
      v-if="!hasClassSelected && activeTab !== 'settings'"
      type="info"
      text
      dense
      class="mb-0"
    >
      Chọn ít nhất một lớp ở ô "Lọc theo lớp" để xem cảnh báo.
    </v-alert>

    <!-- Alert Tabs Content Area -->
    <v-tabs
      v-show="hasClassSelected || activeTab === 'settings'"
      v-model="activeTab"
      class="d-none"
    >
      <!-- Hidden standard tabs since we trigger them via KPI cards -->
      <v-tab href="#quota">Quota</v-tab>
      <v-tab href="#careless">Careless</v-tab>
      <v-tab href="#delete">Delete</v-tab>
      <v-tab href="#easy">Easy</v-tab>
      <v-tab href="#create">Create</v-tab>
      <v-tab href="#streak">Streak</v-tab>
      <v-tab href="#settings">Settings</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="bg-transparent mt-2">
      <!-- TAB 1: QUOTA ALERTS -->
      <v-tab-item value="quota" class="pa-0">
        <v-row>
          <v-col
            cols="12"
            :md="selectedStudent ? 7 : 12"
            class="transition-all"
          >
            <v-card outlined elevation="0" class="table-card">
              <v-card-title
                class="py-2 px-4 subtitle-2 font-weight-bold quota-table-header d-flex align-center"
              >
                <span>Danh sách học sinh chưa hoàn thành chỉ tiêu hôm qua</span>
                <v-btn
                  v-if="totals.quota > 0"
                  small
                  outlined
                  color="primary"
                  class="ml-3 copy-alert-btn"
                  @click.stop="copyAlertMessage('quota')"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy thông báo
                </v-btn>
                <div
                  class="d-flex align-center ml-auto"
                  v-if="totals.quota > 0"
                >
                  <span
                    class="caption grey--text text--darken-3 mr-2 font-weight-bold"
                  >
                    {{
                      (pagination.quota.page - 1) *
                        pagination.quota.itemsPerPage +
                      1
                    }}-{{
                      Math.min(
                        pagination.quota.page * pagination.quota.itemsPerPage,
                        totals.quota
                      )
                    }}
                    trong {{ totals.quota }}
                  </span>
                  <v-btn
                    icon
                    small
                    :disabled="pagination.quota.page <= 1"
                    @click="pagination.quota.page--"
                    class="mr-1"
                  >
                    <v-icon small>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="
                      pagination.quota.page * pagination.quota.itemsPerPage >=
                      totals.quota
                    "
                    @click="pagination.quota.page++"
                  >
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </v-card-title>
              <v-divider />
              <v-data-table
                :headers="headers.quota"
                :items="data.quota"
                :loading="loading.quota"
                :page.sync="pagination.quota.page"
                :items-per-page.sync="pagination.quota.itemsPerPage"
                :server-items-length="totals.quota"
                :footer-props="{
                  'items-per-page-options': [10, 20, 50, 100],
                }"
                no-data-text="Không có học sinh nào chưa hoàn thành chỉ tiêu hôm qua"
                class="quota-table font-weight-medium"
                mobile-breakpoint="0"
                @click:row="selectStudentForDetail"
              >
              </v-data-table>
            </v-card>
          </v-col>
          <v-col v-if="selectedStudent" cols="12" md="5" class="transition-all">
            <student-cards-panel
              :student="selectedStudent"
              :loading="detailLoading"
              @close="selectedStudent = null"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 2: CARELESS ALERTS -->
      <v-tab-item value="careless" class="pa-0">
        <v-row>
          <v-col
            cols="12"
            :md="selectedStudent ? 7 : 12"
            class="transition-all"
          >
            <v-card outlined elevation="0" class="table-card">
              <v-card-title
                class="py-2 px-4 subtitle-2 font-weight-bold careless-table-header d-flex align-center"
              >
                <span>Danh sách học sinh học ẩu trong ngày</span>
                <v-btn
                  v-if="totals.careless > 0"
                  small
                  outlined
                  color="warning"
                  class="ml-3 copy-alert-btn"
                  @click.stop="copyAlertMessage('careless')"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy thông báo
                </v-btn>
                <div
                  class="d-flex align-center ml-auto"
                  v-if="totals.careless > 0"
                >
                  <span
                    class="caption grey--text text--darken-3 mr-2 font-weight-bold"
                  >
                    {{
                      (pagination.careless.page - 1) *
                        pagination.careless.itemsPerPage +
                      1
                    }}-{{
                      Math.min(
                        pagination.careless.page *
                          pagination.careless.itemsPerPage,
                        totals.careless
                      )
                    }}
                    trong {{ totals.careless }}
                  </span>
                  <v-btn
                    icon
                    small
                    :disabled="pagination.careless.page <= 1"
                    @click="pagination.careless.page--"
                    class="mr-1"
                  >
                    <v-icon small>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="
                      pagination.careless.page *
                        pagination.careless.itemsPerPage >=
                      totals.careless
                    "
                    @click="pagination.careless.page++"
                  >
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </v-card-title>
              <v-divider />
              <v-data-table
                :headers="headers.careless"
                :items="data.careless"
                :loading="loading.careless"
                :page.sync="pagination.careless.page"
                :items-per-page.sync="pagination.careless.itemsPerPage"
                :server-items-length="totals.careless"
                :footer-props="{
                  'items-per-page-options': [10, 20, 50, 100],
                }"
                no-data-text="Không có học sinh nào học ẩu trong ngày"
                class="careless-table font-weight-medium"
                mobile-breakpoint="0"
                @click:row="selectStudentForDetail"
              >
                <template v-slot:[`item.timeAvg`]="{ item }">
                  <span class="orange--text font-weight-bold">
                    {{
                      item.timeAvg !== undefined && item.timeAvg !== null
                        ? Number(item.timeAvg).toFixed(1)
                        : "-"
                    }}s
                  </span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
          <v-col v-if="selectedStudent" cols="12" md="5" class="transition-all">
            <student-cards-panel
              :student="selectedStudent"
              :loading="detailLoading"
              @close="selectedStudent = null"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 3: DELETE ALERTS -->
      <v-tab-item value="delete" class="pa-0">
        <v-row>
          <v-col
            cols="12"
            :md="selectedStudent ? 7 : 12"
            class="transition-all"
          >
            <v-card outlined elevation="0" class="table-card">
              <v-card-title
                class="py-2 px-4 subtitle-2 font-weight-bold delete-table-header d-flex align-center"
              >
                <span>Danh sách học sinh xoá nhiều thẻ trong tuần này</span>
                <v-btn
                  v-if="totals.delete > 0"
                  small
                  outlined
                  color="red"
                  class="ml-3 copy-alert-btn"
                  @click.stop="copyAlertMessage('delete')"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy thông báo
                </v-btn>
                <div
                  class="d-flex align-center ml-auto"
                  v-if="totals.delete > 0"
                >
                  <span
                    class="caption grey--text text--darken-3 mr-2 font-weight-bold"
                  >
                    {{
                      (pagination.delete.page - 1) *
                        pagination.delete.itemsPerPage +
                      1
                    }}-{{
                      Math.min(
                        pagination.delete.page * pagination.delete.itemsPerPage,
                        totals.delete
                      )
                    }}
                    trong {{ totals.delete }}
                  </span>
                  <v-btn
                    icon
                    small
                    :disabled="pagination.delete.page <= 1"
                    @click="pagination.delete.page--"
                    class="mr-1"
                  >
                    <v-icon small>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="
                      pagination.delete.page * pagination.delete.itemsPerPage >=
                      totals.delete
                    "
                    @click="pagination.delete.page++"
                  >
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </v-card-title>
              <v-divider />
              <v-data-table
                :headers="headers.delete"
                :items="data.delete"
                :loading="loading.delete"
                :page.sync="pagination.delete.page"
                :items-per-page.sync="pagination.delete.itemsPerPage"
                :server-items-length="totals.delete"
                :footer-props="{
                  'items-per-page-options': [10, 20, 50, 100],
                }"
                no-data-text="Không có học sinh nào xoá quá nhiều thẻ trong tuần này"
                class="delete-table font-weight-medium"
                mobile-breakpoint="0"
                @click:row="selectStudentForDetail"
              >
                <template v-slot:[`item.deletedCount`]="{ item }">
                  <span class="red--text font-weight-bold">{{
                    item.deletedCount
                  }}</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
          <v-col v-if="selectedStudent" cols="12" md="5" class="transition-all">
            <student-cards-panel
              :student="selectedStudent"
              :loading="detailLoading"
              @close="selectedStudent = null"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 4: EASY ALERTS -->
      <v-tab-item value="easy" class="pa-0">
        <v-row>
          <v-col
            cols="12"
            :md="selectedStudent ? 7 : 12"
            class="transition-all"
          >
            <v-card outlined elevation="0" class="table-card">
              <v-card-title
                class="py-2 px-4 subtitle-2 font-weight-bold easy-table-header d-flex align-center"
              >
                <span>Danh sách học sinh ấn dễ quá nhiều trong tuần này</span>
                <v-btn
                  v-if="totals.easy > 0"
                  small
                  outlined
                  color="purple"
                  class="ml-3 copy-alert-btn"
                  @click.stop="copyAlertMessage('easy')"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy thông báo
                </v-btn>
                <div class="d-flex align-center ml-auto" v-if="totals.easy > 0">
                  <span
                    class="caption grey--text text--darken-3 mr-2 font-weight-bold"
                  >
                    {{
                      (pagination.easy.page - 1) *
                        pagination.easy.itemsPerPage +
                      1
                    }}-{{
                      Math.min(
                        pagination.easy.page * pagination.easy.itemsPerPage,
                        totals.easy
                      )
                    }}
                    trong {{ totals.easy }}
                  </span>
                  <v-btn
                    icon
                    small
                    :disabled="pagination.easy.page <= 1"
                    @click="pagination.easy.page--"
                    class="mr-1"
                  >
                    <v-icon small>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="
                      pagination.easy.page * pagination.easy.itemsPerPage >=
                      totals.easy
                    "
                    @click="pagination.easy.page++"
                  >
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </v-card-title>
              <v-divider />
              <v-data-table
                :headers="headers.easy"
                :items="data.easy"
                :loading="loading.easy"
                :page.sync="pagination.easy.page"
                :items-per-page.sync="pagination.easy.itemsPerPage"
                :server-items-length="totals.easy"
                :footer-props="{
                  'items-per-page-options': [10, 20, 50, 100],
                }"
                no-data-text="Không có học sinh nào ấn dễ quá nhiều trong tuần này"
                class="easy-table font-weight-medium"
                mobile-breakpoint="0"
                @click:row="selectStudentForDetail"
              >
                <template v-slot:[`item.easyCount`]="{ item }">
                  <span class="purple--text font-weight-bold">{{
                    item.easyCount
                  }}</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
          <v-col v-if="selectedStudent" cols="12" md="5" class="transition-all">
            <student-cards-panel
              :student="selectedStudent"
              :loading="detailLoading"
              @close="selectedStudent = null"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 5: CARD CREATION ALERTS -->
      <v-tab-item value="create" class="pa-0">
        <v-row>
          <v-col
            cols="12"
            :md="selectedStudent ? 7 : 12"
            class="transition-all"
          >
            <v-card outlined elevation="0" class="table-card">
              <v-card-title
                class="py-2 px-4 subtitle-2 font-weight-bold create-table-header d-flex align-center"
              >
                <span>Danh sách học sinh tạo nhiều thẻ trong tuần này</span>
                <v-btn
                  v-if="totals.create > 0"
                  small
                  outlined
                  color="teal"
                  class="ml-3 copy-alert-btn"
                  @click.stop="copyAlertMessage('create')"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy thông báo
                </v-btn>
                <div
                  class="d-flex align-center ml-auto"
                  v-if="totals.create > 0"
                >
                  <span
                    class="caption grey--text text--darken-3 mr-2 font-weight-bold"
                  >
                    {{
                      (pagination.create.page - 1) *
                        pagination.create.itemsPerPage +
                      1
                    }}-{{
                      Math.min(
                        pagination.create.page * pagination.create.itemsPerPage,
                        totals.create
                      )
                    }}
                    trong {{ totals.create }}
                  </span>
                  <v-btn
                    icon
                    small
                    :disabled="pagination.create.page <= 1"
                    @click="pagination.create.page--"
                    class="mr-1"
                  >
                    <v-icon small>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="
                      pagination.create.page * pagination.create.itemsPerPage >=
                      totals.create
                    "
                    @click="pagination.create.page++"
                  >
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </v-card-title>
              <v-divider />
              <v-data-table
                :headers="headers.create"
                :items="data.create"
                :loading="loading.create"
                :page.sync="pagination.create.page"
                :items-per-page.sync="pagination.create.itemsPerPage"
                :server-items-length="totals.create"
                :footer-props="{
                  'items-per-page-options': [10, 20, 50, 100],
                }"
                no-data-text="Không có học sinh nào tạo quá nhiều thẻ trong tuần này"
                class="create-table font-weight-medium"
                mobile-breakpoint="0"
                @click:row="selectStudentForDetail"
              >
                <template v-slot:[`item.createdCount`]="{ item }">
                  <span class="teal--text font-weight-bold">{{
                    item.createdCount
                  }}</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
          <v-col v-if="selectedStudent" cols="12" md="5" class="transition-all">
            <student-cards-panel
              :student="selectedStudent"
              :loading="detailLoading"
              @close="selectedStudent = null"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 6: INCOMPLETE STREAK ALERTS -->
      <v-tab-item value="streak" class="pa-0">
        <v-row>
          <v-col
            cols="12"
            :md="selectedStreakStudent ? 7 : 12"
            class="transition-all"
          >
            <v-card outlined elevation="0" class="table-card">
              <v-card-title
                class="py-2 px-4 subtitle-2 font-weight-bold streak-table-header d-flex align-center"
              >
                <span>
                  Chưa hoàn thành {{ settings.streakThreshold }} ngày liên tiếp
                  trở lên, tính đến hiện tại ({{
                    formatDate(currentLogicalDate)
                  }})
                </span>
                <v-btn
                  v-if="warningCount > 0"
                  small
                  outlined
                  color="deep-orange"
                  class="ml-3 copy-alert-btn"
                  @click.stop="copyAlertMessage('streak')"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy thông báo
                </v-btn>
                <v-chip
                  v-if="warningCount > 0"
                  color="red lighten-5"
                  small
                  class="ml-2 error--text font-weight-bold"
                >
                  {{ warningCount }} bạn cảnh báo
                </v-chip>
                <div
                  class="d-flex align-center ml-auto"
                  v-if="totals.streak > 0"
                >
                  <span
                    class="caption grey--text text--darken-3 mr-2 font-weight-bold"
                  >
                    {{
                      (pagination.streak.page - 1) *
                        pagination.streak.itemsPerPage +
                      1
                    }}-{{
                      Math.min(
                        pagination.streak.page * pagination.streak.itemsPerPage,
                        totals.streak
                      )
                    }}
                    trong {{ totals.streak }}
                  </span>
                  <v-btn
                    icon
                    small
                    :disabled="pagination.streak.page <= 1"
                    @click="pagination.streak.page--"
                    class="mr-1"
                  >
                    <v-icon small>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="
                      pagination.streak.page * pagination.streak.itemsPerPage >=
                      totals.streak
                    "
                    @click="pagination.streak.page++"
                  >
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </v-card-title>
              <v-divider />
              <v-data-table
                :headers="headers.streak"
                :items="data.streak"
                :loading="loading.streak"
                :page.sync="pagination.streak.page"
                :items-per-page.sync="pagination.streak.itemsPerPage"
                :server-items-length="totals.streak"
                :footer-props="{
                  'items-per-page-options': [5, 10],
                }"
                :item-class="streakRowClass"
                no-data-text="Không có học sinh nào chưa hoàn thành trong ngày này"
                class="streak-table font-weight-medium"
                mobile-breakpoint="0"
                @click:row="selectStreakStudent"
              >
                <template v-slot:[`item.streak`]="{ item }">
                  <v-chip
                    :color="item.isStreakWarning ? 'error' : 'grey lighten-1'"
                    small
                    text-color="white"
                    class="font-weight-bold"
                  >
                    {{ item.streak }} ngày
                  </v-chip>
                </template>
              </v-data-table>
            </v-card>
          </v-col>

          <v-col
            v-if="selectedStreakStudent"
            cols="12"
            md="5"
            class="transition-all"
          >
            <v-card outlined class="detail-panel" elevation="0">
              <v-card-title class="py-2 px-4 detail-header d-flex align-center">
                <v-icon left color="white">mdi-history</v-icon>
                <span class="white--text text-subtitle-2 font-weight-bold"
                  >Chi tiết học sinh</span
                >
                <v-spacer />
                <v-btn icon small dark @click="selectedStreakStudent = null">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text class="pt-4">
                <div class="text-h6 font-weight-bold text--primary">
                  {{ selectedStreakStudent.fullName }}
                </div>
                <div class="grey--text text--darken-1 body-2 mb-4">
                  {{ selectedStreakStudent.userName }} · Lớp
                  {{ selectedStreakStudent.className || "—" }}
                </div>

                <v-row dense class="mb-4">
                  <v-col cols="4">
                    <div class="stat-box error-tint">
                      <div class="text-h5 font-weight-black error--text">
                        {{ detailSummary.currentStreak }}
                      </div>
                      <div
                        class="caption grey--text text--darken-1 font-weight-bold"
                      >
                        Liên tiếp
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="4">
                    <div class="stat-box warning-tint">
                      <div
                        class="text-h5 font-weight-black orange--text text--darken-2"
                      >
                        {{ detailSummary.incompleteDays }}
                      </div>
                      <div
                        class="caption grey--text text--darken-1 font-weight-bold"
                      >
                        Chưa xong
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="4">
                    <div class="stat-box primary-tint">
                      <div class="text-h5 font-weight-black primary--text">
                        {{ detailSummary.totalDays }}
                      </div>
                      <div
                        class="caption grey--text text--darken-1 font-weight-bold"
                      >
                        Tổng ngày
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-divider />

              <v-data-table
                :headers="headers.detailDays"
                :items="detailDays"
                :loading="detailLoading"
                hide-default-footer
                disable-pagination
                dense
                no-data-text="Không có dữ liệu trong khoảng này"
                class="detail-table font-weight-medium"
                mobile-breakpoint="0"
              >
                <template v-slot:[`item.date`]="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                <template v-slot:[`item.status`]="{ item }">
                  <v-chip
                    :color="statusColor(item)"
                    x-small
                    text-color="white"
                    class="font-weight-bold"
                  >
                    {{ statusText(item) }}
                  </v-chip>
                </template>
                <template v-slot:[`item.cards`]="{ item }">
                  <span
                    v-if="item.hasReport"
                    class="font-weight-bold text--primary"
                    >{{ item.totalCardsInDay }}</span
                  >
                  <span v-else class="grey--text">—</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 6: SETTINGS & PENALTIES -->
      <v-tab-item value="settings" class="pa-0">
        <v-row>
          <!-- Quota settings card -->
          <v-col cols="12" md="6" class="pa-3">
            <v-card outlined class="h-100 settings-card" elevation="0">
              <v-card-title class="subtitle-1 font-weight-bold py-3 px-4">
                <v-icon left color="error">mdi-calendar-alert</v-icon>
                Chưa hoàn thành chỉ tiêu
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4 px-4">
                <div class="d-flex align-center flex-wrap" style="gap: 12px">
                  <v-text-field
                    v-model.number="penalties.penaltyQuota.points"
                    label="Điểm trừ vi phạm"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                    style="max-width: 150px; min-width: 120px"
                    class="flex-grow-1"
                  />
                </div>
                <div class="mt-4">
                  <v-checkbox
                    v-model="penalties.penaltyQuota.enabled"
                    label="Bật phạt vi phạm chỉ tiêu học tập"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Careless settings card -->
          <v-col cols="12" md="6" class="pa-3">
            <v-card outlined class="h-100 settings-card" elevation="0">
              <v-card-title class="subtitle-1 font-weight-bold py-3 px-4">
                <v-icon left color="warning">mdi-speedometer</v-icon>
                Học ẩu (Học quá nhanh)
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4 px-4">
                <div class="d-flex align-center flex-wrap" style="gap: 12px">
                  <v-text-field
                    v-model.number="settings.minTimePerCard"
                    label="Thời gian tối thiểu / thẻ"
                    type="number"
                    min="1"
                    outlined
                    dense
                    suffix="giây"
                    hide-details
                    style="max-width: 220px; min-width: 160px"
                    class="flex-grow-1"
                  />
                  <v-text-field
                    v-model.number="penalties.penaltyCareless.points"
                    label="Điểm trừ vi phạm"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                    style="max-width: 150px; min-width: 120px"
                    class="flex-grow-1"
                  />
                </div>
                <div class="mt-4">
                  <v-checkbox
                    v-model="penalties.penaltyCareless.enabled"
                    label="Bật phạt vi phạm học ẩu"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Delete settings card -->
          <v-col cols="12" md="6" class="pa-3">
            <v-card outlined class="h-100 settings-card" elevation="0">
              <v-card-title class="subtitle-1 font-weight-bold py-3 px-4">
                <v-icon left color="red">mdi-delete-alert</v-icon>
                Xóa thẻ nhiều
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4 px-4">
                <div class="d-flex align-center flex-wrap" style="gap: 12px">
                  <v-text-field
                    v-model.number="settings.maxDeletesPerWeek"
                    label="Số thẻ xoá tối đa / tuần"
                    type="number"
                    min="1"
                    outlined
                    dense
                    suffix="thẻ"
                    hide-details
                    style="max-width: 220px; min-width: 160px"
                    class="flex-grow-1"
                  />
                  <v-text-field
                    v-model.number="penalties.penaltyDelete.points"
                    label="Điểm trừ vi phạm"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                    style="max-width: 150px; min-width: 120px"
                    class="flex-grow-1"
                  />
                </div>
                <div
                  class="d-flex align-center flex-wrap mt-4"
                  style="gap: 16px"
                >
                  <v-checkbox
                    v-model="penalties.penaltyDelete.enabled"
                    label="Bật phạt xóa thẻ nhiều"
                    dense
                    hide-details
                    class="mt-0 mr-4"
                  />
                  <v-checkbox
                    v-model="penalties.penaltyDelete.escalate"
                    label="Tính điểm phạt lũy tiến tăng dần"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Easy settings card -->
          <v-col cols="12" md="6" class="pa-3">
            <v-card outlined class="h-100 settings-card" elevation="0">
              <v-card-title class="subtitle-1 font-weight-bold py-3 px-4">
                <v-icon left color="purple">mdi-thumb-up-alert</v-icon>
                Ấn dễ nhiều
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4 px-4">
                <div class="d-flex align-center flex-wrap" style="gap: 12px">
                  <v-text-field
                    v-model.number="settings.maxEasyPerWeek"
                    label="Số lần ấn dễ tối đa / tuần"
                    type="number"
                    min="1"
                    outlined
                    dense
                    suffix="lần"
                    hide-details
                    style="max-width: 220px; min-width: 160px"
                    class="flex-grow-1"
                  />
                  <v-text-field
                    v-model.number="penalties.penaltyEasy.points"
                    label="Điểm trừ vi phạm"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                    style="max-width: 150px; min-width: 120px"
                    class="flex-grow-1"
                  />
                </div>
                <div class="mt-4">
                  <v-checkbox
                    v-model="penalties.penaltyEasy.enabled"
                    label="Bật phạt ấn dễ quá nhiều"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Card creation settings card -->
          <v-col cols="12" md="6" class="pa-3">
            <v-card outlined class="h-100 settings-card" elevation="0">
              <v-card-title class="subtitle-1 font-weight-bold py-3 px-4">
                <v-icon left color="teal">mdi-card-plus</v-icon>
                Tạo thẻ nhiều
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4 px-4">
                <div class="d-flex align-center flex-wrap" style="gap: 12px">
                  <v-text-field
                    v-model.number="settings.maxCreatesPerWeek"
                    label="Số thẻ tạo tối đa / tuần"
                    type="number"
                    min="1"
                    outlined
                    dense
                    suffix="thẻ"
                    hide-details
                    style="max-width: 220px; min-width: 160px"
                    class="flex-grow-1"
                  />
                  <v-text-field
                    v-model.number="penalties.penaltyCreate.points"
                    label="Điểm trừ vi phạm"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                    style="max-width: 150px; min-width: 120px"
                    class="flex-grow-1"
                  />
                </div>
                <div class="mt-4">
                  <v-checkbox
                    v-model="penalties.penaltyCreate.enabled"
                    label="Bật phạt tạo thẻ quá nhiều"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Streak settings card -->
          <v-col cols="12" md="6" class="pa-3">
            <v-card outlined class="h-100 settings-card" elevation="0">
              <v-card-title class="subtitle-1 font-weight-bold py-3 px-4">
                <v-icon left color="deep-orange"
                  >mdi-account-multiple-minus</v-icon
                >
                Liên tiếp chưa xong chỉ tiêu
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4 px-4">
                <div class="d-flex align-center flex-wrap" style="gap: 12px">
                  <v-text-field
                    v-model.number="settings.streakThreshold"
                    label="Ngưỡng cảnh báo (ngày liên tiếp)"
                    type="number"
                    min="1"
                    outlined
                    dense
                    suffix="ngày"
                    hide-details
                    style="max-width: 220px; min-width: 160px"
                    class="flex-grow-1"
                  />
                  <v-text-field
                    v-model.number="penalties.penaltyStreak.points"
                    label="Điểm trừ vi phạm"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                    style="max-width: 150px; min-width: 120px"
                    class="flex-grow-1"
                  />
                </div>
                <div
                  class="d-flex align-center flex-wrap mt-4"
                  style="gap: 16px"
                >
                  <v-checkbox
                    v-model="penalties.penaltyStreak.enabled"
                    label="Bật phạt liên tiếp chưa xong"
                    dense
                    hide-details
                    class="mt-0 mr-4"
                  />
                  <v-checkbox
                    v-model="penalties.penaltyStreak.escalate"
                    label="Tính điểm phạt lũy tiến tăng dần"
                    dense
                    hide-details
                    class="mt-0"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Per-class Google Sheets Integration Section -->
        <v-row class="mt-4">
          <v-col cols="12" class="pa-3">
            <v-card outlined class="settings-card" elevation="0">
              <v-card-title class="subtitle-1 font-weight-bold py-3 px-4">
                <v-icon left color="green darken-2"
                  >mdi-google-spreadsheet</v-icon
                >
                Liên kết Google Sheets theo lớp
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4 px-4">
                <div
                  v-for="cls in classes"
                  :key="'sheet-' + cls.id"
                  class="mb-4"
                >
                  <div class="subtitle-2 font-weight-bold mb-2">
                    {{ cls.className }}
                  </div>
                  <v-row>
                    <v-col cols="12" md="8" class="py-1">
                      <v-text-field
                        :value="cls.googleSpreadsheetId || ''"
                        @input="cls.googleSpreadsheetId = $event"
                        label="Đường dẫn Google Sheet (URL hoặc ID)"
                        placeholder="https://docs.google.com/spreadsheets/d/.../edit"
                        outlined
                        dense
                        hide-details
                        class="mb-1"
                      />
                    </v-col>
                    <v-col cols="12" md="4" class="py-1">
                      <v-text-field
                        :value="cls.googleSheetTabName || ''"
                        @input="cls.googleSheetTabName = $event"
                        label="Tên trang tính (Tab name)"
                        placeholder="Sheet1"
                        outlined
                        dense
                        hide-details
                        class="mb-1"
                      />
                    </v-col>
                  </v-row>
                  <v-divider class="mt-2" />
                </div>

                <v-btn
                  color="green darken-2"
                  dark
                  small
                  :loading="savingClassSheets"
                  @click="saveClassSheetConfigs"
                  class="mt-2"
                >
                  <v-icon left small>mdi-content-save</v-icon>
                  Lưu cấu hình Sheet
                </v-btn>

                <v-alert
                  v-if="settings.serviceAccountEmail"
                  type="info"
                  outlined
                  border="left"
                  class="mt-4 mb-0 caption py-2 px-3"
                  color="blue darken-2"
                >
                  Để hệ thống có quyền ghi dữ liệu báo cáo, vui lòng mở tệp
                  Google Sheet của bạn, bấm nút
                  <strong>Chia sẻ (Share)</strong> và phân quyền
                  <strong>Người chỉnh sửa (Editor)</strong> cho tài khoản
                  Service Account sau:
                  <div
                    class="mt-1 font-weight-bold d-flex align-center select-all"
                  >
                    <code>{{ settings.serviceAccountEmail }}</code>
                  </div>
                </v-alert>
                <v-alert
                  v-else
                  type="warning"
                  outlined
                  border="left"
                  class="mt-4 mb-0 caption py-2 px-3"
                >
                  Không tìm thấy tệp credentials.json trên máy chủ. Vui lòng
                  thiết lập để kết nối Google Sheets.
                </v-alert>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Save Button -->
        <v-btn
          color="primary"
          large
          elevation="4"
          :loading="savingConfig || savingPenalties"
          @click="saveAllSettings"
          class="font-weight-bold settings-floating-btn"
        >
          <v-icon left>mdi-content-save-all</v-icon>
          Lưu tất cả cấu hình & điểm phạt
        </v-btn>
      </v-tab-item>
    </v-tabs-items>
    <!-- Global Alert Toast (Snackbar) -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
      right
      elevation="6"
    >
      <div class="d-flex align-center py-1">
        <v-icon dark left class="mr-2">
          {{
            snackbar.color === "success"
              ? "mdi-check-circle"
              : snackbar.color === "error"
              ? "mdi-alert-circle"
              : "mdi-information"
          }}
        </v-icon>
        <span class="font-weight-medium body-2">{{ snackbar.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
          class="font-weight-bold"
        >
          Đóng
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "@/services/axios";
import StudentCardsPanel from "@/components/admin/StudentCardsPanel.vue";

const HEADERS = {
  quota: [
    { text: "Họ tên", value: "fullName", sortable: true },
    { text: "Tài khoản", value: "userName", sortable: false },
    { text: "Lớp", value: "className", sortable: true },
    { text: "Ngày", value: "date", sortable: false },
  ],
  careless: [
    { text: "Họ tên", value: "fullName", sortable: true },
    { text: "Tài khoản", value: "userName", sortable: false },
    { text: "Lớp", value: "className", sortable: true },
    { text: "Thời gian TB / thẻ", value: "timeAvg", sortable: true },
    { text: "Ngày vi phạm", value: "date", sortable: false },
  ],
  delete: [
    { text: "Họ tên", value: "fullName", sortable: true },
    { text: "Tài khoản", value: "userName", sortable: false },
    { text: "Lớp", value: "className", sortable: true },
    { text: "Số thẻ đã xoá trong tuần", value: "deletedCount", sortable: true },
  ],
  easy: [
    { text: "Họ tên", value: "fullName", sortable: true },
    { text: "Tài khoản", value: "userName", sortable: false },
    { text: "Lớp", value: "className", sortable: true },
    { text: "Số lần ấn dễ trong tuần", value: "easyCount", sortable: true },
  ],
  create: [
    { text: "Họ tên", value: "fullName", sortable: true },
    { text: "Tài khoản", value: "userName", sortable: false },
    { text: "Lớp", value: "className", sortable: true },
    { text: "Số thẻ đã tạo trong tuần", value: "createdCount", sortable: true },
  ],
  streak: [
    { text: "Họ tên", value: "fullName", sortable: true },
    { text: "Tài khoản", value: "userName", sortable: false },
    { text: "Lớp", value: "className", sortable: true },
    { text: "Liên tiếp chưa xong", value: "streak", sortable: true },
  ],
  detailDays: [
    { text: "Ngày", value: "date", sortable: false },
    { text: "Trạng thái", value: "status", sortable: false },
    { text: "Số thẻ", value: "cards", sortable: false, align: "end" },
  ],
};

const pad = (n) => String(n).padStart(2, "0");
const fmt = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * The day currently in progress, matching the backend's resolveLogicalDate().
 * A study day runs 04:00 -> 04:00, so before 04:00 the logical day is still
 * yesterday.
 *
 * The "liên tiếp chưa hoàn thành" streak is a live, ongoing status — it should
 * always reflect right now, not whatever historical date the admin has picked
 * in the shared date filter below (that filter is for the quota/careless/etc.
 * alerts, which need a *closed* day to be meaningful).
 */
function currentLogicalDayStr() {
  const now = new Date();
  const d = new Date(now);
  if (now.getHours() < 4) d.setDate(d.getDate() - 1);
  return fmt(d);
}

/**
 * The most recent day whose results are final.
 *
 * A study day runs 04:00 -> 04:00, so the day in progress is not settled until
 * tomorrow morning — at midday almost every student still reads as "chưa hoàn
 * thành" simply because they have not studied yet. The alerts page therefore
 * defaults to the last *closed* day for the alerts that need one.
 */
function lastClosedDayStr() {
  const now = new Date();
  const d = new Date(now);
  // Before 04:00 the logical day is still yesterday, so the last closed day is
  // the one before that.
  d.setDate(d.getDate() - (now.getHours() < 4 ? 2 : 1));
  return fmt(d);
}

function todayStr() {
  return fmt(new Date());
}

function getTodayMinusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default {
  name: "ManageAlerts",
  components: {
    StudentCardsPanel,
  },
  data() {
    return {
      activeTab: "quota",
      dateRangeFrom: getTodayMinusDays(7),
      // Defaults to the last closed day, not today — see lastClosedDayStr().
      dateRangeTo: lastClosedDayStr(),
      fromDateMenu: false,
      toDateMenu: false,
      loading: {
        quota: false,
        careless: false,
        delete: false,
        easy: false,
        create: false,
        streak: false,
      },
      savingConfig: false,
      detailLoading: false,
      classes: [],
      selectedClasses: [],
      selectedAlertTypes: [
        "quota",
        "careless",
        "delete",
        "easy",
        "create",
        "streak",
      ],
      alertTypeOptions: [
        { text: "Chưa hoàn thành chỉ tiêu", value: "quota" },
        { text: "Học ẩu", value: "careless" },
        { text: "Xoá thẻ nhiều", value: "delete" },
        { text: "Ấn dễ nhiều", value: "easy" },
        { text: "Tạo thẻ nhiều", value: "create" },
        { text: "Liên tiếp chưa xong", value: "streak" },
      ],
      manualViolationDialog: false,
      submittingViolation: false,
      manualViolation: {
        userId: null,
        alertType: "quota",
        detail: "",
        severity: "warning",
      },
      students: [],
      pagination: {
        quota: { page: 1, itemsPerPage: 20 },
        careless: { page: 1, itemsPerPage: 20 },
        delete: { page: 1, itemsPerPage: 20 },
        easy: { page: 1, itemsPerPage: 20 },
        create: { page: 1, itemsPerPage: 20 },
        streak: { page: 1, itemsPerPage: 5 },
      },
      data: {
        quota: [],
        careless: [],
        delete: [],
        easy: [],
        create: [],
        streak: [],
      },
      // Full (unpaginated) name lists, used only to build the copyable message.
      allNames: {
        quota: [],
        careless: [],
        delete: [],
        easy: [],
        create: [],
        streak: [],
      },
      // Date labels + thresholds the backend resolved for the current query.
      alertMeta: {
        targetDate: "",
        weekFrom: "",
        weekTo: "",
        minTimePerCard: 5,
        maxDeletesPerWeek: 10,
        maxEasyPerWeek: 50,
        maxCreatesPerWeek: 100,
        streakThreshold: 5,
      },
      settings: {
        minNumberCardLearn: null,
        minTimePerCard: 5,
        maxDeletesPerWeek: 10,
        maxEasyPerWeek: 50,
        maxCreatesPerWeek: 100,
        streakThreshold: 5,
        serviceAccountEmail: "",
      },
      penalties: {
        penaltyQuota: { points: 10, enabled: true, escalate: false },
        penaltyCareless: { points: 5, enabled: true, escalate: false },
        penaltyDelete: { points: 8, enabled: true, escalate: true },
        penaltyEasy: { points: 3, enabled: true, escalate: false },
        penaltyCreate: { points: 3, enabled: true, escalate: false },
        penaltyStreak: { points: 10, enabled: true, escalate: true },
      },
      selectedStreakStudent: null,
      selectedStudent: null,
      detailDays: [],
      detailSummary: { totalDays: 0, incompleteDays: 0, currentStreak: 0 },
      settingsPanel: {
        quota: undefined,
        careless: undefined,
        delete: undefined,
        easy: undefined,
        create: undefined,
        streak: undefined,
      },
      savingPenalties: false,
      savingClassSheets: false,
      headers: HEADERS,
      snackbar: { show: false, message: "", color: "success" },
      totals: {
        quota: 0,
        careless: 0,
        delete: 0,
        easy: 0,
        create: 0,
        streak: 0,
        streakWarning: 0,
      },
      isResetting: false,
    };
  },

  computed: {
    warningCount() {
      return this.totals.streakWarning;
    },
    // Caps both date pickers: a day in progress has no final result to show.
    lastClosedDate() {
      return lastClosedDayStr();
    },
    // The streak table always reports "as of now" — see currentLogicalDayStr().
    currentLogicalDate() {
      return currentLogicalDayStr();
    },
    hasClassSelected() {
      return this.selectedClasses.length > 0;
    },
    kpis() {
      return [
        {
          title: "Chưa xong chỉ tiêu",
          count: this.totals.quota,
          value: "quota",
          icon: "mdi-calendar-alert",
          bgColor: "red lighten-5",
          iconColor: "error",
          textColor: "error",
        },
        {
          title: "Học ẩu",
          count: this.totals.careless,
          value: "careless",
          icon: "mdi-speedometer",
          bgColor: "orange lighten-5",
          iconColor: "warning",
          textColor: "warning",
        },
        {
          title: "Xóa thẻ nhiều",
          count: this.totals.delete,
          value: "delete",
          icon: "mdi-delete-alert",
          bgColor: "red lighten-5",
          iconColor: "red",
          textColor: "red",
        },
        {
          title: "Ấn dễ nhiều",
          count: this.totals.easy,
          value: "easy",
          icon: "mdi-thumb-up-alert",
          bgColor: "purple lighten-5",
          iconColor: "purple",
          textColor: "purple",
        },
        {
          title: "Tạo thẻ nhiều",
          count: this.totals.create,
          value: "create",
          icon: "mdi-card-plus",
          bgColor: "teal lighten-5",
          iconColor: "teal",
          textColor: "teal",
        },
        {
          title: "Liên tiếp chưa xong",
          count: this.totals.streak,
          value: "streak",
          icon: "mdi-account-multiple-minus",
          bgColor: "orange lighten-5",
          iconColor: "deep-orange",
          textColor: "deep-orange",
        },
        {
          title: "Cấu hình hệ thống",
          count: "Cài đặt",
          value: "settings",
          icon: "mdi-cog",
          bgColor: "blue lighten-5",
          iconColor: "primary",
          textColor: "primary",
        },
      ];
    },
  },

  watch: {
    activeTab() {
      this.selectedStudent = null;
      this.selectedStreakStudent = null;
    },
    "pagination.quota.page"() {
      if (!this.isResetting) this.fetchQuotaAlerts();
    },
    "pagination.quota.itemsPerPage"() {
      if (!this.isResetting) this.fetchQuotaAlerts();
    },
    "pagination.careless.page"() {
      if (!this.isResetting) this.fetchCarelessAlerts();
    },
    "pagination.careless.itemsPerPage"() {
      if (!this.isResetting) this.fetchCarelessAlerts();
    },
    "pagination.delete.page"() {
      if (!this.isResetting) this.fetchDeleteAlerts();
    },
    "pagination.delete.itemsPerPage"() {
      if (!this.isResetting) this.fetchDeleteAlerts();
    },
    "pagination.easy.page"() {
      if (!this.isResetting) this.fetchEasyAlerts();
    },
    "pagination.easy.itemsPerPage"() {
      if (!this.isResetting) this.fetchEasyAlerts();
    },
    "pagination.create.page"() {
      if (!this.isResetting) this.fetchCreateCardAlerts();
    },
    "pagination.create.itemsPerPage"() {
      if (!this.isResetting) this.fetchCreateCardAlerts();
    },
    "pagination.streak.page"() {
      if (!this.isResetting) this.fetchStreakAlerts();
    },
    "pagination.streak.itemsPerPage"() {
      if (!this.isResetting) this.fetchStreakAlerts();
    },
  },

  async created() {
    await Promise.all([
      this.fetchClasses(),
      this.fetchStudents(),
      this.fetchConfig(),
      this.fetchPenalties(),
    ]);
    // No alerts are fetched until a class is chosen — see hasClassSelected.
  },

  methods: {
    async fetchClasses() {
      try {
        const res = await axios.get("admin/classes");
        if (res.data.status_code === "200") {
          this.classes = res.data.data || [];
        }
      } catch {
        // optional
      }
    },

    async fetchStudents() {
      try {
        const res = await axios.get("admin/accounts");
        if (res.data.status_code === "200") {
          this.students = (res.data.data || []).filter((s) => s.role === 0);
        }
      } catch {
        // optional
      }
    },

    async fetchConfig() {
      try {
        const res = await axios.get("admin/notification/config");
        if (res.data.status_code === "200") {
          this.settings = { ...this.settings, ...res.data.data };
        }
      } catch {
        // use defaults
      }
    },

    /**
     * Every alert query fans out over the selected classes, and running it
     * across all of them scans ~340 students and takes minutes. So the page
     * starts empty and asks the admin to pick a class first.
     */
    clearAlertData() {
      for (const key of Object.keys(this.data)) {
        this.data[key] = [];
        this.totals[key] = 0;
      }
      this.totals.streakWarning = 0;
      this.allNames = {
        quota: [],
        careless: [],
        delete: [],
        easy: [],
        create: [],
        streak: [],
      };
    },

    async fetchAlerts() {
      if (!this.hasClassSelected) {
        this.clearAlertData();
        return;
      }
      // Prevent watcher triggers from causing double fetches during reset
      this.isResetting = true;
      for (const key in this.pagination) {
        this.pagination[key].page = 1;
      }
      this.isResetting = false;

      await Promise.all([
        this.fetchQuotaAlerts(),
        this.fetchCarelessAlerts(),
        this.fetchDeleteAlerts(),
        this.fetchEasyAlerts(),
        this.fetchCreateCardAlerts(),
        this.fetchStreakAlerts(),
      ]);
    },

    /**
     * Store the unpaginated name list and the date/threshold labels the backend
     * resolved, so the copy button can build the message without guessing.
     */
    captureAlertMeta(type, payload) {
      this.allNames = { ...this.allNames, [type]: payload.allNames || [] };
      if (payload.meta) {
        this.alertMeta = { ...this.alertMeta, ...payload.meta };
      }
    },

    async fetchQuotaAlerts() {
      if (!this.hasClassSelected) return;
      if (!this.selectedClasses.length) return;
      this.loading.quota = true;
      try {
        const res = await axios.post("admin/notification/alerts", {
          classIds: this.selectedClasses,
          dateFrom: this.dateRangeFrom,
          dateTo: this.dateRangeTo,
          type: "quota",
          page: this.pagination.quota.page,
          limit: this.pagination.quota.itemsPerPage,
        });
        if (res.data.status_code === "200") {
          this.data.quota = res.data.data.quotaAlerts || [];
          this.totals.quota = res.data.data.total || 0;
          this.captureAlertMeta("quota", res.data.data);
        } else {
          this.showSnackbar(
            res.data.message || "Tải cảnh báo chỉ tiêu thất bại!",
            "error"
          );
        }
      } catch {
        this.showSnackbar("Lỗi khi tải cảnh báo chỉ tiêu!", "error");
      } finally {
        this.loading.quota = false;
      }
    },

    async fetchCarelessAlerts() {
      if (!this.hasClassSelected) return;
      if (!this.selectedClasses.length) return;
      this.loading.careless = true;
      try {
        const res = await axios.post("admin/notification/alerts", {
          classIds: this.selectedClasses,
          dateFrom: this.dateRangeFrom,
          dateTo: this.dateRangeTo,
          type: "careless",
          page: this.pagination.careless.page,
          limit: this.pagination.careless.itemsPerPage,
        });
        if (res.data.status_code === "200") {
          this.data.careless = res.data.data.carelessAlerts || [];
          this.totals.careless = res.data.data.total || 0;
          this.captureAlertMeta("careless", res.data.data);
        } else {
          this.showSnackbar(
            res.data.message || "Tải cảnh báo học ẩu thất bại!",
            "error"
          );
        }
      } catch {
        this.showSnackbar("Lỗi khi tải cảnh báo học ẩu!", "error");
      } finally {
        this.loading.careless = false;
      }
    },

    async fetchDeleteAlerts() {
      if (!this.hasClassSelected) return;
      if (!this.selectedClasses.length) return;
      this.loading.delete = true;
      try {
        const res = await axios.post("admin/notification/alerts", {
          classIds: this.selectedClasses,
          dateFrom: this.dateRangeFrom,
          dateTo: this.dateRangeTo,
          type: "delete",
          page: this.pagination.delete.page,
          limit: this.pagination.delete.itemsPerPage,
        });
        if (res.data.status_code === "200") {
          this.data.delete = res.data.data.deleteAlerts || [];
          this.totals.delete = res.data.data.total || 0;
          this.captureAlertMeta("delete", res.data.data);
        } else {
          this.showSnackbar(
            res.data.message || "Tải cảnh báo xóa thẻ thất bại!",
            "error"
          );
        }
      } catch {
        this.showSnackbar("Lỗi khi tải cảnh báo xóa thẻ!", "error");
      } finally {
        this.loading.delete = false;
      }
    },

    async fetchEasyAlerts() {
      if (!this.hasClassSelected) return;
      if (!this.selectedClasses.length) return;
      this.loading.easy = true;
      try {
        const res = await axios.post("admin/notification/alerts", {
          classIds: this.selectedClasses,
          dateFrom: this.dateRangeFrom,
          dateTo: this.dateRangeTo,
          type: "easy",
          page: this.pagination.easy.page,
          limit: this.pagination.easy.itemsPerPage,
        });
        if (res.data.status_code === "200") {
          this.data.easy = res.data.data.easyAlerts || [];
          this.totals.easy = res.data.data.total || 0;
          this.captureAlertMeta("easy", res.data.data);
        } else {
          this.showSnackbar(
            res.data.message || "Tải cảnh báo ấn dễ thất bại!",
            "error"
          );
        }
      } catch {
        this.showSnackbar("Lỗi khi tải cảnh báo ấn dễ!", "error");
      } finally {
        this.loading.easy = false;
      }
    },

    async fetchCreateCardAlerts() {
      if (!this.hasClassSelected) return;
      if (!this.selectedClasses.length) return;
      this.loading.create = true;
      try {
        const res = await axios.post("admin/notification/alerts", {
          classIds: this.selectedClasses,
          dateFrom: this.dateRangeFrom,
          dateTo: this.dateRangeTo,
          type: "create",
          page: this.pagination.create.page,
          limit: this.pagination.create.itemsPerPage,
        });
        if (res.data.status_code === "200") {
          this.data.create = res.data.data.createAlerts || [];
          this.totals.create = res.data.data.total || 0;
          this.captureAlertMeta("create", res.data.data);
        } else {
          this.showSnackbar(
            res.data.message || "Tải cảnh báo tạo thẻ thất bại!",
            "error"
          );
        }
      } catch {
        this.showSnackbar("Lỗi khi tải cảnh báo tạo thẻ!", "error");
      } finally {
        this.loading.create = false;
      }
    },

    async fetchStreakAlerts() {
      if (!this.hasClassSelected) return;
      if (!this.selectedClasses.length) return;
      this.loading.streak = true;
      try {
        const res = await axios.post("admin/learning-mng/incomplete-students", {
          classId: this.selectedClasses,
          // Always "now" — not dateRangeTo, which is a filter for the other,
          // closed-day alerts. See currentLogicalDayStr().
          date: currentLogicalDayStr(),
          streakThreshold: this.settings.streakThreshold,
          page: this.pagination.streak.page,
          limit: this.pagination.streak.itemsPerPage,
        });
        if (res.data.status_code === "200") {
          if (
            res.data.data &&
            typeof res.data.data === "object" &&
            "list" in res.data.data
          ) {
            this.data.streak = res.data.data.list || [];
            this.totals.streak = res.data.data.total || 0;
            this.totals.streakWarning = res.data.data.warningCount || 0;
            this.captureAlertMeta("streak", res.data.data);
          } else {
            this.data.streak = res.data.data || [];
            this.totals.streak = this.data.streak.length;
            const warnings = this.data.streak.filter((i) => i.isStreakWarning);
            this.totals.streakWarning = warnings.length;
            this.captureAlertMeta("streak", {
              allNames: warnings.map((i) => i.fullName),
            });
          }
        } else {
          this.showSnackbar(
            res.data.message || "Tải cảnh báo streak thất bại!",
            "error"
          );
        }
      } catch {
        this.showSnackbar("Lỗi khi tải cảnh báo streak!", "error");
      } finally {
        this.loading.streak = false;
      }
    },

    /**
     * Compose the Vietnamese notification line for one alert type, ready to
     * paste into a chat group. Thresholds and dates come from the backend so
     * the wording always matches the data that produced the list.
     */
    buildAlertMessage(type) {
      const m = this.alertMeta;
      const names = (this.allNames[type] || []).join(", ");
      if (!names) return "";

      // The page reports the last *closed* day, so on the 10th that is the 9th.
      // "Hôm qua" only holds for that default — name the date outright for any
      // other day the admin picks.
      const day =
        this.dateRangeTo === lastClosedDayStr()
          ? `Hôm qua (ngày ${m.targetDate})`
          : `Ngày ${m.targetDate}`;
      const week = `Trong tuần này (${m.weekFrom}-${m.weekTo})`;

      const sentences = {
        quota: `${day} có những bạn sau không hoàn thành cas`,
        careless: `${day} có những bạn sau học dưới ${m.minTimePerCard} giây/thẻ`,
        delete: `${week} có những bạn sau xoá trên ${m.maxDeletesPerWeek} thẻ`,
        easy: `${week} có những bạn sau ấn dễ trên ${m.maxEasyPerWeek} lần`,
        create: `${week} có những bạn sau tạo trên ${m.maxCreatesPerWeek} thẻ`,
        streak: `${week} có những bạn sau có ${m.streakThreshold} ngày liên tiếp trở lên không hoàn thành cas`,
      };

      return `[CASTORY] ${sentences[type]}: ${names}`;
    },

    async copyAlertMessage(type) {
      const message = this.buildAlertMessage(type);
      if (!message) {
        this.showSnackbar("Không có học sinh nào để copy!", "warning");
        return;
      }

      try {
        await this.writeToClipboard(message);
        const count = (this.allNames[type] || []).length;
        this.showSnackbar(`Đã copy thông báo (${count} học sinh)!`, "success");
      } catch {
        this.showSnackbar("Trình duyệt chặn copy, vui lòng thử lại!", "error");
      }
    },

    /**
     * navigator.clipboard needs a secure context; fall back to a hidden
     * textarea so copy still works when the admin panel is served over HTTP.
     */
    async writeToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        const ok = document.execCommand("copy");
        if (!ok) throw new Error("execCommand copy failed");
      } finally {
        document.body.removeChild(textarea);
      }
    },

    async saveAllSettings() {
      this.savingConfig = true;
      this.savingPenalties = true;
      this.savingClassSheets = true;
      try {
        const [configRes, penaltyRes] = await Promise.all([
          axios.put("admin/notification/config", this.settings),
          axios.put("admin/notification/penalties", this.penalties),
          ...this.classes.map((cls) =>
            axios.put(`admin/class/${cls.id}`, {
              googleSpreadsheetId: cls.googleSpreadsheetId || null,
              googleSheetTabName: cls.googleSheetTabName || null,
            })
          ),
        ]);
        if (
          configRes.data.status_code === "200" &&
          penaltyRes.data.status_code === "200"
        ) {
          this.settings = { ...this.settings, ...configRes.data.data };
          this.penalties = penaltyRes.data.data;
          this.savingConfig = false;
          this.savingPenalties = false;
          this.showSnackbar("Đã lưu tất cả cấu hình thành công!", "success");
          this.fetchAlerts();
        } else {
          this.showSnackbar("Có lỗi xảy ra khi lưu cấu hình!", "error");
        }
      } catch (err) {
        this.showSnackbar("Lỗi khi kết nối đến máy chủ!", "error");
      } finally {
        this.savingConfig = false;
        this.savingPenalties = false;
        this.savingClassSheets = false;
      }
    },

    async saveClassSheetConfigs() {
      this.savingClassSheets = true;
      try {
        await Promise.all(
          this.classes.map((cls) =>
            axios.put(`admin/class/${cls.id}`, {
              googleSpreadsheetId: cls.googleSpreadsheetId || null,
              googleSheetTabName: cls.googleSheetTabName || null,
            })
          )
        );
        this.showSnackbar("Đã lưu cấu hình Sheet theo lớp!", "success");
      } catch {
        this.showSnackbar("Lỗi khi lưu cấu hình Sheet!", "error");
      } finally {
        this.savingClassSheets = false;
      }
    },

    async selectStreakStudent(item) {
      this.selectedStreakStudent = item;
      this.detailLoading = true;
      try {
        const res = await axios.post(
          `admin/learning-mng/student-detail/${item.userId}`,
          {
            toDate: this.dateRangeTo,
            streakThreshold: this.settings.streakThreshold,
          }
        );
        if (res.data.status_code === "200") {
          const data = res.data.data || {};
          this.detailDays = data.days || [];
          this.detailSummary = data.summary || {
            totalDays: 0,
            incompleteDays: 0,
            currentStreak: 0,
          };
        } else {
          this.showSnackbar(
            res.data.message || "Tải chi tiết thất bại!",
            "error"
          );
        }
      } catch {
        this.showSnackbar("Lỗi khi tải chi tiết!", "error");
      } finally {
        this.detailLoading = false;
      }
    },

    handleFilterChange() {
      this.selectedStreakStudent = null;
      // An empty selection is a valid state — the page simply shows nothing and
      // asks for a class. Snapping back to the first class here meant that
      // clearing the filter silently selected whatever class happened to sort
      // first, which is not what the admin asked for.
      this.fetchAlerts();
    },

    streakRowClass(item) {
      return item.isStreakWarning ? "warning-row" : "";
    },

    statusText(item) {
      if (!item.hasReport) return "Không có bài";
      return item.status === 1 ? "Hoàn thành" : "Chưa xong";
    },

    statusColor(item) {
      if (!item.hasReport) return "grey";
      return item.status === 1 ? "success" : "error";
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },

    showSnackbar(message, color) {
      this.snackbar = { show: true, message, color };
    },

    selectStudentForDetail(student) {
      const sId = student.userId || student.id;
      if (sId) {
        this.$router.push(`/browse-admin/student/${sId}`);
      }
    },

    resetFilters() {
      this.dateRangeFrom = lastClosedDayStr();
      this.dateRangeTo = lastClosedDayStr();
      // Clearing the filter means clearing it: the admin picks the class again.
      this.selectedClasses = [];
      this.selectedStreakStudent = null;
      this.selectedStudent = null;
      this.handleFilterChange();
    },

    async fetchPenalties() {
      try {
        const res = await axios.get("admin/notification/penalties");
        if (res.data.status_code === "200") {
          this.penalties = res.data.data;
        }
      } catch {
        // use defaults
      }
    },

    async submitManualViolation() {
      if (!this.manualViolation.userId || !this.manualViolation.alertType) {
        this.showSnackbar("Vui lòng chọn học sinh và loại vi phạm", "warning");
        return;
      }

      this.submittingViolation = true;
      try {
        const res = await axios.post("admin/violations/log-manual", {
          userId: this.manualViolation.userId,
          alertType: this.manualViolation.alertType,
          detail: this.manualViolation.detail || "",
          severity: this.manualViolation.severity,
        });

        if (res.data.status_code === "200") {
          const violation = res.data.data;
          this.showSnackbar(
            `Vi phạm đã được log: ${violation.fullName} - ${violation.penaltyPoints} điểm - báo ${violation.shouldNotifyDate}`,
            "success"
          );

          // Reset form
          this.manualViolationDialog = false;
          this.manualViolation = {
            userId: null,
            alertType: "quota",
            detail: "",
            severity: "warning",
          };

          // Refresh alerts
          await this.fetchAlerts();
        } else {
          this.showSnackbar(
            res.data.message || "Log vi phạm thất bại!",
            "error"
          );
        }
      } catch (error) {
        this.showSnackbar(
          error.response?.data?.message || error.message,
          "error"
        );
      } finally {
        this.submittingViolation = false;
      }
    },

    getTodayMinusDays(days) {
      return getTodayMinusDays(days);
    },

    getTodayStr() {
      return todayStr();
    },
  },
};
</script>

<style lang="scss" scoped>
.alerts-page {
  /* Layout transitions */
  .transition-all {
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  }

  /* KPI Overview Cards styling */
  .kpi-card {
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    cursor: pointer;
    border-radius: 12px !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    background: #ffffff;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06) !important;
      border-color: rgba(0, 0, 0, 0.15) !important;
    }

    &.active {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04) !important;
      border-width: 2px !important;
      margin: -1px; /* offset extra border width */
    }

    &.quota-kpi.active {
      border-color: #ff5252 !important;
      background: #ffebee !important;
    }

    &.careless-kpi.active {
      border-color: #fb8c00 !important;
      background: #fff3e0 !important;
    }

    &.delete-kpi.active {
      border-color: #e53935 !important;
      background: #ffebee !important;
    }

    &.easy-kpi.active {
      border-color: #9c27b0 !important;
      background: #f3e5f5 !important;
    }

    &.streak-kpi.active {
      border-color: #ff5722 !important;
      background: #fbe9e7 !important;
    }

    &.settings-kpi.active {
      border-color: #2196f3 !important;
      background: #e3f2fd !important;
    }
  }

  /* Filter Card styling */
  .filter-card {
    border-radius: 12px !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02) !important;
  }

  /* Table Card styling */
  .table-card {
    border-radius: 12px !important;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
  }

  /* Customized Light Headers for Tables */
  .quota-table-header {
    background-color: #ffebee !important;
    color: #c62828 !important;
  }
  .careless-table-header {
    background-color: #fff3e0 !important;
    color: #ef6c00 !important;
  }
  .delete-table-header {
    background-color: #ffebee !important;
    color: #c62828 !important;
  }
  .easy-table-header {
    background-color: #f3e5f5 !important;
    color: #6a1b9a !important;
  }
  .create-table-header {
    background-color: #e0f2f1 !important;
    color: #00695c !important;
  }
  .streak-table-header {
    background-color: #fbe9e7 !important;
    color: #d84315 !important;
  }

  /* Keep the copy action compact so it never wraps the table title */
  .copy-alert-btn {
    flex: 0 0 auto;
    text-transform: none;
    letter-spacing: 0;
    background-color: #fff;
  }

  ::v-deep .v-data-table__wrapper {
    height: auto !important;
    max-height: none !important;
    overflow-y: visible !important;
    overflow-x: auto !important;
  }

  ::v-deep .v-data-table {
    height: auto !important;
    max-height: none !important;
    .v-data-table-header {
      background-color: #f8f9fa !important;
      th {
        color: #495057 !important;
        font-weight: 700 !important;
        font-size: 0.875rem !important;
      }
    }

    tbody tr {
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #f8f9fa !important;
      }
    }
  }

  ::v-deep .warning-row {
    background-color: #ffebee !important;

    &:hover {
      background-color: #ffcdd2 !important;
    }
  }

  /* Detail panels */
  .detail-panel {
    border-radius: 12px !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  }

  .detail-header {
    background: linear-gradient(135deg, #453fe3 0%, #1e1b9b 100%) !important;
  }

  /* Tinted Stats Box */
  .stat-box {
    text-align: center;
    padding: 12px 6px;
    border-radius: 10px;

    &.error-tint {
      background: #ffebee;
    }
    &.warning-tint {
      background: #fff3e0;
    }
    &.primary-tint {
      background: #e8eaf6;
    }
  }

  /* Settings cards */
  .settings-card {
    border-radius: 12px !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: rgba(69, 63, 227, 0.3) !important;
    }
  }

  /* Floating Settings Save Button */
  .settings-floating-btn {
    position: fixed !important;
    bottom: 24px;
    right: 24px;
    z-index: 1000;
    border-radius: 28px !important;
    box-shadow: 0 8px 24px rgba(69, 63, 227, 0.3) !important;
    padding: 0 24px !important;
    height: 50px !important;
    transition: all 0.2s ease-in-out !important;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 10px 28px rgba(69, 63, 227, 0.45) !important;
    }
  }
}
</style>
